import React, { useState } from "react";

import {
  CancelOutlined,
  CheckCircleOutlined,
  Circle,
  ExpandLess,
  ExpandMore,
  MoreVert,
  OpenInNewOutlined,
} from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { enqueueSnackbar } from "notistack";
import { connect } from "react-redux";
import { NavLink } from "react-router-dom";

import { Tooltip } from "../../../../common";
import {
  deleteResume as deleteResumeAction,
  rejectResume as rejectResumeAction,
} from "../../../../redux/slices/resumeSlice";
import { interview_stages } from "../../../../static/data";
import { getCamelCaseWord, getStageColor } from "../../../../utils/functions";

/* =============================================================================
<Row />
============================================================================= */
const Row = ({
  isRowExpended,
  onExpand,
  page,
  interview,
  deleteResume,
  rejectResume,
}) => {
  const [menuLoading, setMenuLoding] = useState(false);
  const stage = interview?.stage || "";
  const violations = interview?.results?.length
    ? interview?.results[0]?.violations_detected
    : {};
  const violationDetected =
    typeof violations === "object"
      ? Object.values(violations).reduce(
          (accumulator, currentValue) =>
            currentValue ? (accumulator += 1) : accumulator,
          0
        )
      : 0;

  const handleDelete = async () => {
    if (menuLoading == "delete") return;
    try {
      setMenuLoding("delete");

      await deleteResume({ ids: [interview?.id], page });
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    } finally {
      setMenuLoding(false);
    }
  };

  const handleReject = async () => {
    if (menuLoading === "reject") return;
    try {
      setMenuLoding("reject");

      await rejectResume({ ids: [interview?.id] });
    } catch (e) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
    } finally {
      setMenuLoding(false);
    }
  };

  return (
    <TableRow hover role="checkbox" tabIndex={-1}>
      <TableCell align="left" sx={{ border: "none" }}>
        <IconButton onClick={onExpand}>
          {!isRowExpended ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        <Link
          component={NavLink}
          to={`/interview-candidates/${interview?.job_post_id}/candidate-result/${interview?.id}`}
          fontSize="14px"
          target="_blank"
          rel="noopener noreferrer"
          fontWeight={600}
        >
          {interview?.candidate_name ? interview?.candidate_name : "--"}
        </Link>
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        {interview?.processing_status === "processing" ? (
          <Typography color="#999" fontSize="14px" fontWeight={600}>
            Processing
          </Typography>
        ) : interview?.stage === "Interview started" ? (
          <Box display="flex">
            <Box
              sx={{
                bgcolor: "#75DA1C",
                borderRadius: "30px",
                px: 2,
                py: 1,
              }}
            >
              <Typography color="#FFFFFF" fontSize="14px" fontWeight={600}>
                Generating
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box display="flex">
            {interview?.overall_score !== null &&
            interview?.overall_score !== undefined ? (
              <Box
                sx={{
                  bgcolor: "purple.main",
                  borderRadius: "30px",
                  px: 2,
                  py: 1,
                }}
              >
                <Typography color="white" fontSize="13px" fontWeight={600}>
                  Overall score {interview?.overall_score}%
                </Typography>
              </Box>
            ) : (
              <Typography color="#999" fontSize="14px" fontWeight={600}>
                --
              </Typography>
            )}
          </Box>
        )}
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        {interview?.processing_status === "processing" ? (
          <Typography color="#999" fontSize="14px" fontWeight={600}>
            Processing
          </Typography>
        ) : interview?.interview_passed == null ? (
          <Typography color="#999" fontSize="14px" fontWeight={600}>
            --
          </Typography>
        ) : (
          <Box display="flex">
            <Box
              sx={{
                bgcolor: interview?.interview_passed
                  ? "purple.main"
                  : "#FF5B5B",
                borderRadius: "30px",
                px: 2,
                py: 1,
              }}
            >
              <Typography color="white" fontSize="13px" fontWeight={600}>
                {interview?.interview_passed ? "Passed" : "Not Passed"}{" "}
                {interview?.interview_score ?? "--"}%
              </Typography>
            </Box>
          </Box>
        )}
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        <Box display="flex">
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              bgcolor: getStageColor(stage),
              borderRadius: "30px",
              px: 2,
              py: 1,
              maxWidth: "200px", // Adjust width dynamically
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="white" fontSize="13px" fontWeight={600}>
              {getCamelCaseWord(stage)}
            </Typography>
            <Tooltip
              title={
                <Box>
                  {interview_stages.map((s, index) => {
                    const currentStageIndex = interview_stages.findIndex(
                      (_stag) => _stag.toLowerCase() === stage.toLowerCase()
                    );

                    const isChecked = index <= currentStageIndex;

                    return (
                      <Box
                        key={s}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1.5}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Circle
                            sx={{
                              width: "10px",
                              height: "10px",
                              color: getStageColor(s),
                            }}
                          />
                          <Typography
                            color={getStageColor(s)}
                            fontSize="17px"
                            fontWeight={500}
                            mr={2}
                          >
                            {s}
                          </Typography>
                        </Box>
                        {isChecked ? (
                          <CheckCircleOutlined
                            sx={{
                              color: "#80D63E",
                              width: "18px",
                              height: "18px",
                            }}
                          />
                        ) : (
                          <CancelOutlined
                            sx={{
                              color: "#F24B4B",
                              width: "18px",
                              height: "18px",
                            }}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              }
            >
              <Circle sx={{ color: "white", width: "5px", height: "5px" }} />
            </Tooltip>
          </Box>
        </Box>
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        <Typography fontSize="13px" fontWeight={500}>
          {interview?.interview_attempted
            ? dayjs(interview?.interview_attempted).format("DD/MM/YYYY")
            : "--"}
        </Typography>
      </TableCell>
      <TableCell align="left" sx={{ border: "none" }}>
        <Typography fontSize="13px" fontWeight={500}>
          {interview?.resume_score
            ? dayjs(interview?.created_at).format("DD/MM/YYYY")
            : "--"}
        </Typography>
      </TableCell>

      <TableCell align="left" sx={{ border: "none" }}>
        <Typography
          color={violationDetected === 0 ? "purple.main" : "#FF5B5B"}
          fontSize="13px"
          fontWeight={600}
        >
          {violationDetected === 0
            ? "No violation detected"
            : `${violationDetected || 0} violation detected`}
        </Typography>
      </TableCell>
      <TableCell align="left" sx={{ border: "none" }}>
        <Box display="flex" gap={1}>
          <Link
            component={NavLink}
            to={`/interview-candidates/${interview?.job_post_id}/candidate-result/${interview?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            mt={0.5}
          >
            <OpenInNewOutlined
              sx={{ width: "22px", height: "22px", color: "#0891B2" }}
            />
          </Link>
          <PopupState variant="popover" popupId="Rowdldsf">
            {(popupState) => (
              <React.Fragment>
                <IconButton sx={{ p: 0.2 }} {...bindTrigger(popupState)}>
                  <MoreVert />
                </IconButton>
                <Menu {...bindMenu(popupState)}>
                  <MenuItem sx={{ py: 1 }}>
                    <Typography
                      fontFamily="Outfit"
                      fontWeight={500}
                      fontSize="12px"
                    >
                      Send results
                    </Typography>
                  </MenuItem>
                  {/* <MenuItem sx={{ py: 1 }} onClick={handleReject}>
                  {menuLoading === "reject" ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Typography
                      fontFamily="Outfit"
                      fontWeight={500}
                      fontSize="12px"
                    >
                      Reject
                    </Typography>
                  )}
                </MenuItem> */}
                  {/* <MenuItem sx={{ py: 1 }} onClick={handleDelete}>
                  {menuLoading === "delete" ? (
                    <CircularProgress size={18} sx={{ color: "red" }} />
                  ) : (
                    <Typography
                      color="red"
                      fontFamily="Outfit"
                      fontWeight={500}
                      fontSize="12px"
                    >
                      Delete candidate
                    </Typography>
                  )}
                </MenuItem> */}
                </Menu>
              </React.Fragment>
            )}
          </PopupState>
        </Box>
      </TableCell>
    </TableRow>
  );
};

const mapDipatchToProps = {
  deleteResume: deleteResumeAction,
  rejectResume: rejectResumeAction,
};

/* Export
============================================================================= */
export default connect(null, mapDipatchToProps)(React.memo(Row));
